import Highcharts from 'highcharts';
import { useState } from 'react';
import { RothConversionChartDataDTO, TaxBracketDTO } from '../../RothConversion.types';
import { formatCurrency, formatPercentage } from '../../../../../../../../CommonUtils/formatNumber';
import { useTheme } from '../../../../../../../../theme/theme';

interface ChartSeries {
  type: string;
  name: string;
  color: string;
  data: number[][];
  stack?: string;
  legendIndex?: number;
}

export interface TaxBracket extends TaxBracketDTO {
  selected: boolean;
}

const getBracketSeriesList = (brackets: TaxBracket[]) =>
  brackets.map(({ value, data, color, selected }) => ({
    type: 'line',
    name: `Tax Bracket (${formatPercentage(value)})`,
    color,
    data,
    visible: selected,
  }));

interface UseRothConversionChartParams {
  data: RothConversionChartDataDTO;
}

interface UseRothConversionChartReturn {
  data: {
    series: ChartSeries[];
    categories: number[];
    customSettings: Highcharts.Options;
    brackets: TaxBracket[];
  };
  actions: {
    setBrackets(brackets: TaxBracket[]): void;
  };
}

export const useRothConversionChart = ({
  data,
}: UseRothConversionChartParams): UseRothConversionChartReturn => {
  const theme = useTheme();

  const { brackets: rawBrackets = [], maxTaxBracket } = data;

  const [brackets, setBrackets] = useState<TaxBracket[]>(() =>
    rawBrackets
      .sort((a, b) => a.value - b.value)
      .map(bracket => ({
        ...bracket,
        selected: maxTaxBracket?.value === bracket.value,
        value: bracket.value / 100,
      })),
  );

  const series = [
    {
      type: 'column',
      name: 'Current',
      data: data.series.current,
      color: theme.colors.primary['300'],
      stack: 'current',
      legendIndex: 0,
    },

    {
      type: 'column',
      name: 'Conversion Amount',
      data: data.series.recommended.map(([year]) => [year, data.conversionAmount[year]]),
      color: theme.colors.primary['700'],
      stack: 'proposed',
      legendIndex: 2,
    },

    {
      type: 'column',
      name: 'Proposed',
      data: data.series.recommended.map(([year, value]) => [
        year,
        value - data.conversionAmount[year],
      ]),
      color: theme.colors.primary['500'],
      stack: 'proposed',
      legendIndex: 1,
    },

    // ...getBracketSeriesList(bracketsBefore2026),

    // ...getBracketSeriesList(bracketsStarting2026),
    ...getBracketSeriesList(brackets),
  ];

  const categories = data.series.current.map(([year]) => year);

  const customSettings: Highcharts.Options = {
    xAxis: {
      tickInterval: 3,
      tickWidth: 0,
    },

    tooltip: {
      shared: true,
      borderRadius: 10,
      shadow: false,
      useHTML: true,
      backgroundColor: theme.colors.neutral['700'],
      borderColor: theme.colors.neutral['700'],
      shape: 'rect',
      style: {
        color: theme.colors.neutral['100'],
      },
      formatter() {
        return `
            <div
              style="
                font-family: ${theme.fontFamily};
                font-size: ${theme.fontSizes.small};
                color: ${theme.colors.neutral['100']};
              "
            >
              <div>Conversion Details</div>

              <div style="display: flex; align-items: center">
                <span
                  style="
                    width: 8px;
                    height: 8px;
                    border-radius: 4px;
                    margin-right: 4px;
                    background: ${this.x ? data.yearlyTaxBrackets[this.x].color : undefined};
                  "
                ></span>

                Tax Bracket:
                <span
                  style="
                    font-weight: 600;
                    margin-left: 4px;
                  "
                >
                  ${
                    this.x && data.yearlyTaxBrackets[this.x]
                      ? formatPercentage(data.yearlyTaxBrackets[this.x].value)
                      : ''
                  }
                </span>
              </div>
            
                <div>Amount:
                <span style="font-weight: 600; margin-left: 4px;">
                    ${this.x && formatCurrency(data.conversionAmount[this.x])}
                  </span>
                </div>
            </div>
          `;
      },
    },

    plotOptions: {
      line: {
        showInLegend: false,
        marker: {
          enabled: false,
        },
      },

      column: {
        stacking: 'normal',
      },
    },
  };

  return {
    data: {
      series,
      categories,
      customSettings,
      brackets,
    },
    actions: {
      setBrackets,
    },
  };
};
